import { useParams, Link, Navigate } from "react-router-dom";
import { Container, CTAStrip } from "@/components/Sections";
import { RESOURCES } from "@/lib/content";
import { resourcePdfUrl } from "@/lib/api";
import { Download, ArrowLeft, ArrowRight } from "lucide-react";

export default function ResourceDetail() {
  const { slug } = useParams();
  const idx = RESOURCES.findIndex((r) => r.slug === slug);
  if (idx === -1) return <Navigate to="/resources" replace />;

  const r = RESOURCES[idx];
  const next = RESOURCES[(idx + 1) % RESOURCES.length];
  const prev = RESOURCES[(idx - 1 + RESOURCES.length) % RESOURCES.length];

  return (
    <div data-testid={`page-resource-${slug}`} className="bg-[#F6F1E6]">
      <section className="pt-40 md:pt-52 pb-16">
        <Container>
          <Link
            to="/resources"
            data-testid="resource-back"
            className="overline inline-flex items-center gap-2 hover:text-[#C45A3C] transition-colors mb-10"
          >
            <ArrowLeft size={14} /> All resources
          </Link>
          <p className="overline mb-6">
            Resource · {String(idx + 1).padStart(2, "0")} of{" "}
            {String(RESOURCES.length).padStart(2, "0")}
          </p>
          <h1
            data-testid="resource-title"
            className="display-xxl text-5xl md:text-7xl lg:text-[96px] mb-8 leading-[1.02]"
          >
            {r.title}
          </h1>
          <p className="font-serif italic text-2xl md:text-3xl text-[#3F5132] max-w-3xl leading-snug mb-12">
            {r.subtitle}
          </p>
          <a
            href={resourcePdfUrl(r.slug)}
            data-testid={`resource-pdf-${slug}`}
            target="_blank"
            rel="noreferrer"
            className="btn-primary inline-flex"
          >
            <Download size={16} />
            Download PDF brief
          </a>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-24 md:py-32">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <p className="overline mb-5">In this brief</p>
              <ul className="space-y-0">
                {r.topics.map((t, i) => (
                  <li
                    key={t}
                    className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 lg:border-l lg:border-[#1F2A1B]/10 lg:pl-16">
              <p className="overline mb-5">Overview</p>
              <p className="text-[#4F5A48] text-lg md:text-xl leading-relaxed mb-8">
                {r.excerpt}
              </p>
              <p className="text-[#4F5A48] text-base leading-relaxed mb-10">
                The full PDF brief includes plain-language explanations,
                practical guidance for ranger programs and Traditional Owner
                organisations, links to relevant frameworks, and prompts for
                discussion with your team and partners.
              </p>
              <div className="border border-[#1F2A1B]/15 p-8 bg-[#ECE4D2]">
                <p className="overline mb-3">Tailor this brief</p>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1F2A1B] mb-4 leading-snug">
                  Need this customised for your Country and program?
                </h3>
                <Link
                  to="/contact"
                  data-testid={`resource-${slug}-customise`}
                  className="btn-ghost"
                >
                  Request a tailored brief
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="border-t border-[#1F2A1B]/10 py-20">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2A1B]/10">
            <Link
              to={`/resources/${prev.slug}`}
              data-testid="resource-prev"
              className="bg-[#F6F1E6] hover:bg-[#ECE4D2] transition-colors p-8 md:p-10 flex items-center gap-5 group"
            >
              <ArrowLeft
                size={22}
                className="text-[#8A8775] group-hover:text-[#C45A3C] group-hover:-translate-x-1 transition-all"
              />
              <div>
                <p className="overline mb-2">Previous</p>
                <p className="font-serif text-xl md:text-2xl text-[#1F2A1B] group-hover:text-[#3F5132] transition-colors">
                  {prev.title}
                </p>
              </div>
            </Link>
            <Link
              to={`/resources/${next.slug}`}
              data-testid="resource-next"
              className="bg-[#F6F1E6] hover:bg-[#ECE4D2] transition-colors p-8 md:p-10 flex items-center justify-end gap-5 group text-right"
            >
              <div>
                <p className="overline mb-2">Next</p>
                <p className="font-serif text-xl md:text-2xl text-[#1F2A1B] group-hover:text-[#3F5132] transition-colors">
                  {next.title}
                </p>
              </div>
              <ArrowRight
                size={22}
                className="text-[#8A8775] group-hover:text-[#C45A3C] group-hover:translate-x-1 transition-all"
              />
            </Link>
          </div>
        </Container>
      </section>

      <CTAStrip
        heading="Build capability around this topic."
        body="From workforce planning to monitoring frameworks — we partner with organisations on the practical work behind every resource."
        label="Talk to us"
        testid="resource-cta-strip"
      />
    </div>
  );
}
